import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Map from 'ol/Map';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Stack } from '@mui/material';

import { useMapStore } from '../stores/useMapStore';
import { useSeismicStore } from '../stores/useSeismicStore';

interface ScaleBarProps {
    map: Map;
}

export type SeismicScaleBarProps = {
    unitDistance: number
    vertical?: boolean,
    unit: ScaleBarUnit
};

export enum ScaleBarUnit {
    Kilometer = 0,
    Meter = 1,
    Miliseconds = 2,
    Feet = 3,
    Kilofoot = 4
}

export const SeismicScaleBar = ({ unitDistance, vertical = false, unit }: SeismicScaleBarProps) => {
    const map = useMapStore(state => state.map);
    const [segmentWidth, setSegmentWidth] = useState(0);
    const [labels, setLabels] = useState<string[]>([]);
    const [scaleX, scaleY] = useSeismicStore(state => [state.scale.x, state.scale.y]);

    const getUnitLabel = (unit: ScaleBarUnit): string => {
        switch (unit) {
            case ScaleBarUnit.Kilometer:
                return 'km';
            case ScaleBarUnit.Meter:
                return 'm';
            case ScaleBarUnit.Miliseconds:
                return 'ms';
            case ScaleBarUnit.Kilofoot:
                return 'Mi';
            case ScaleBarUnit.Feet:
                return 'ft';
            default:
                return 'km';
        }
    };

    const unitLabel = useMemo(() => getUnitLabel(unit), [unit]);

    const convertToUnit = useCallback((value: number) => {
        if (unit === ScaleBarUnit.Kilometer || unit === ScaleBarUnit.Kilofoot) {
            if (unit === ScaleBarUnit.Kilofoot) {
                return value / 5280;
            }
            return value / 1000;
        }
        else {
            return value;
        }
    }, [unit]);

    const previousUpdateCallbackRef = useRef<() => void>();

    const updateScaleBar = useCallback(() => {
        if (!map) {
            return;
        }

        const view = map.getView();
        const resolution = view.getResolution();

        if (!resolution) {
            return;
        }

        const mapUnitsPerPixel = resolution;

        const segmentLength = Math.ceil(100 * mapUnitsPerPixel);
        const segmentWidthInPixels = segmentLength / mapUnitsPerPixel; // Each segment represents 100 meters
        const numSegments = 5; // Number of segments to display

        setSegmentWidth(segmentWidthInPixels);

        const newLabels: string[] = [];
        const scale = vertical ? scaleY : scaleX;

        for (let i = 0; i < numSegments; i++) {
            const distance = convertToUnit(i * segmentLength * unitDistance) / scale; // Convert meters to kilometers
            newLabels.push(`${distance.toFixed(1)} ${unitLabel}`);
        }

        setLabels(newLabels);
    }, [map, scaleX, scaleY]);

    useEffect(() => {
        if (!map) {
            return;
        }

        if (!!previousUpdateCallbackRef.current) {
            map.removeEventListener('moveend', previousUpdateCallbackRef.current);
        }
        previousUpdateCallbackRef.current = updateScaleBar;

        map.on('moveend', updateScaleBar);
        updateScaleBar();

        return () => map.un('moveend', updateScaleBar);
    }, [updateScaleBar]);

    return (
        <Box
            sx={{
                position: 'absolute',
                bottom: vertical ? `calc(50vh - ${Math.round(segmentWidth * 4 / 2)}px)` : '30px',
                left: vertical ? '10px' : `calc(50vw - ${Math.round(segmentWidth * 4 / 2)}px)`,
                bgcolor: 'transparent',
                borderRadius: 1,
                textAlign: 'center',
                display: 'inline-block',
                zIndex: 999,
                overflowX: 'visible',
            }}
        >
            <Box sx={{ display: 'flex', flexDirection: vertical ? 'column' : 'row', height: vertical ? 'auto' : '15px', width: vertical ? '15px' : 'auto', position: 'relative', boxShadow: 1, border: '1px solid black', zIndex: 9999999 }}>
                {labels.slice(0, labels.length - 1).map((label, index) => (
                    <Box
                        key={index}
                        sx={{
                            width: vertical ? '100%' : `${segmentWidth}px`,
                            height: vertical ? `${segmentWidth}px` : '100%',
                            bgcolor: index % 2 === 0 ? '#f2f2f2' : '#4d4d4d',
                            position: 'relative',
                            '&:not(:last-child)': {
                                borderRight: '1px solid',
                                borderColor: 'divider',
                            },
                        }}
                    />
                ))}
            </Box>
            <Box sx={{ display: 'flex', position: 'relative' }}>
                {labels.map((label, index) => (
                    <Typography
                        key={index}
                        variant="body1"
                        sx={{
                            position: 'absolute',
                            bottom: vertical ? `${(labels.length - index - 1) * segmentWidth}px` : 'auto',
                            left: vertical ? '40px' : `${index * segmentWidth}px`,
                            transform: vertical ? 'translateY(25%)' : 'translateX(-50%)',
                            color: 'black',
                            textWrap: 'nowrap',
                            textShadow: 'white 1px 1px 5px'
                        }}
                        title={label}
                    >
                        {label}
                    </Typography>
                ))}
            </Box>
        </Box>
    );
};
